import { ChangeEvent, FormEvent, useRef, useState } from 'react';
import { useVerifyMfaMutation } from 'app/services';
import { useBodyClass } from 'hooks/useBodyClass';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { track, trackBeforeLeaving } from 'app/analytics';

/**
 * mfa_failed reason, from the verify API's status and its fixed `detail`
 * strings (api/rest_auth/views.py verify_mfa). Never the typed code.
 */
const mfaFailureReason = (err: unknown): string => {
  const { status, data, originalStatus } = (err ?? {}) as {
    status?: unknown;
    data?: { detail?: unknown; restart?: unknown };
    originalStatus?: unknown;
  };
  const detail = typeof data?.detail === 'string' ? data.detail : '';
  if (detail === 'Invalid or expired code') return 'wrong_code';
  if (detail.startsWith('Too many wrong codes')) return 'locked';
  if (detail.startsWith('Your code has expired')) return 'expired';
  if (data?.restart === true) return 'no_login_in_progress';
  if (status === 'FETCH_ERROR') return 'network';
  if (typeof status === 'number' && status >= 500) return 'server_error';
  if (status === 'PARSING_ERROR' && typeof originalStatus === 'number' && originalStatus >= 500) {
    return 'server_error';
  }
  return 'other';
};

// Port of the server-rendered mfa_verify.html — same markup and classes,
// styled by the .mfa-container rules in common.css. Django serves this route
// only while a login is waiting for its code (sdmk.react_views.verify_mfa_page),
// otherwise it redirects to /login/.
//
// Parity with the old page:
//  - VERIFY stays .non-clickable/disabled until the field holds 6 digits.
//  - Only digits go in. The old page filtered keydown, which also swallowed
//    Ctrl/Cmd+V; this filters the value, so paste and autofill work and a
//    pasted "123 456" or a code with a trailing newline is cleaned up.
//  - The clear icon shows while the field is non-empty.
//  - "Remember this device for 90 days" sets the trusted-device cookie.
//  - Errors show the API's text, as the template meant to show the view's
//    (it never did: see has-error below).
// New: when the code can't succeed any more (used up by wrong guesses,
// expired, or no login in progress), a link back to the login page replaces
// the dead end.
const VerifyMfa = () => {
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restart, setRestart] = useState(false);
  const [verifyMfa, { isLoading }] = useVerifyMfaMutation();
  const codeRef = useRef<HTMLInputElement>(null);
  useBodyClass('sign-in');
  useDocumentTitle('Kinetix - Two-Factor Authentication');

  const complete = code.length === 6;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!complete || isLoading) return;
    setError(null);
    try {
      const res = await verifyMfa({ code, remember_device: rememberDevice }).unwrap();
      trackBeforeLeaving('mfa_verified');
      window.location.href = res.redirect || '/';
    } catch (err) {
      track('mfa_failed', { reason: mfaFailureReason(err) });
      const data = (err as { data?: { detail?: unknown; restart?: unknown } })?.data;
      setError(typeof data?.detail === 'string' ? data.detail : 'Verification failed');
      setRestart(data?.restart === true);
      setCode('');
      codeRef.current?.focus();
    }
  };

  return (
    <div className="sign-in-container mfa-container">
      <div className="login">
        <div className="login-logo" />
        {/* has-error: common.css hides .required-error inside a sign-in form
            without it. The old template never set it, so its errors were
            rendered but never visible. */}
        <form
          id="mfa-form"
          className={error ? 'has-error' : ''}
          method="post"
          action="/verify-mfa/"
          onSubmit={onSubmit}
        >
          <h3 className="two-factor-h3">Two-Factor Authentication</h3>
          <p className="two-factor-p">
            A verification code has been sent to your email.
            <br />
            Please enter it below to complete your login.
          </p>
          {error && (
            // The shared .sign-in .required-error rule pins the message
            // absolutely, 70px from the bottom: on this form that is on top of
            // VERIFY and swallows its clicks. Keep it in the flow, above the
            // input, where the template put it.
            <div
              className="required-error"
              role="alert"
              style={{ position: 'static', width: 'auto', fontSize: 12, margin: '8px 0 12px' }}
            >
              {error}
              {restart && (
                <>
                  {' '}
                  <a href="/login/">Back to login</a>
                </>
              )}
            </div>
          )}
          <div className="first-input">
            <input
              ref={codeRef}
              id="code"
              type="text"
              name="code"
              placeholder="Enter 6-digit code"
              pattern="[0-9]{6}"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={onChange}
            />
            <i
              className="text-clear icon-close-1"
              style={{ display: code ? undefined : 'none' }}
              onClick={() => setCode('')}
            />
          </div>
          <div className="checkbox">
            <ul>
              <li className="flex-li">
                <input
                  id="remember_device"
                  type="checkbox"
                  name="remember_device"
                  value="1"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                />
                <label htmlFor="remember_device" className="remember_device_label">
                  Remember this device for 90 days
                </label>
              </li>
            </ul>
          </div>
          <div className="button">
            <input
              type="submit"
              value="VERIFY"
              className={complete && !isLoading ? '' : 'non-clickable'}
              disabled={!complete || isLoading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyMfa;
